import React, { useState, useEffect, Fragment } from "react";
import api from "../../../../api";
import {
 Card,
 Row,
 Col,
 Badge,
 Table,
 Button,
 ProgressBar,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import VerifiedCopyActions from "../../Common/VerifiedCopyActions";

const MeasurementSheetTab = () => {
 const { id: workOrderId } = useParams();
 const navigate = useNavigate();

 const [sheets, setSheets] = useState([]);
 const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0 });
 const [loading, setLoading] = useState(true);
 const [previewing, setPreviewing] = useState(false);
 const [billedIds, setBilledIds] = useState(new Set());

 useEffect(() => {
 if (workOrderId) fetchSheets();
 }, [workOrderId]);

 const fetchSheets = async () => {
 try {
 setLoading(true);
 const [res, billingRes] = await Promise.all([
 api.get("/measurement-sheets", { params: { workOrder: workOrderId, limit: "all" } }),
 api.get("/billing", { params: { workOrder: workOrderId, limit: "all" } }),
 ]);

 if (res.data.success) {
 const data = res.data.data;
 setSheets(data);

 const approved = data.filter((x) => x.status === "Approved").length;
 const pending = data.filter((x) => x.status !== "Approved").length;

 setStats({ total: data.length, approved, pending });
 }

 if (billingRes.data.success) {
 const ids = new Set();
 (billingRes.data.data || []).forEach((b) => {
 (b.linkedReports || []).forEach((group) => {
 (group.reportIds || []).forEach((id) => ids.add(String(id)));
 });
 });
 setBilledIds(ids);
 }
 } catch (err) {
 Swal.fire("Error", "Failed to load Measurement Sheets", "error");
 } finally {
 setLoading(false);
 }
 };

 const handleDeleteSheet = async (id) => {
 const isBilled = billedIds.has(String(id));
 if (isBilled) {
 return Swal.fire("Forbidden", "This sheet is already linked to a bill and cannot be deleted.", "warning");
 }

 const confirm = await Swal.fire({
 title: "Are you sure?",
 text: "You won't be able to revert this!",
 icon: "warning",
 showCancelButton: true,
 confirmButtonColor: "#d33",
 cancelButtonColor: "#3085d6",
 confirmButtonText: "Yes, delete it!"
 });

 if (confirm.isConfirmed) {
 try {
 const res = await api.delete(`/measurement-sheets/${id}`);
 if (res.data.success) {
 Swal.fire("Deleted!", "Your sheet has been deleted.", "success");
 fetchSheets();
 }
 } catch (err) {
 Swal.fire("Error", err.response?.data?.message || "Failed to delete sheet", "error");
 }
 }
 };

 const approvalPercent = stats.total === 0 ? 0 : (stats.approved / stats.total) * 100;

 const formatDate = (d) =>
 d ? new Date(d).toLocaleDateString("en-IN", {
 day: "2-digit",
 month: "short",
 year: "numeric",
 })
 : "—";

 const getBadge = (st) => {
 switch (st) {
 case "Approved": return "success";
 case "Rejected": return "danger";
 case "Submitted": return "warning";
 default: return "secondary";
 }
 };

 const handleDownloadFile = async (id, type) => {
 try {
 setPreviewing(true);
 const res = await api.get(`/measurement-sheets/${id}/${type}`, { responseType: "blob" });
 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.setAttribute("download", `MeasurementSheet_${id}.${type === 'pdf' ? 'pdf' : 'xlsx'}`);
 document.body.appendChild(link);
 link.click();
 link.remove();
 } catch (err) {
 Swal.fire("Error", "Failed to download file", "error");
 } finally {
 setPreviewing(false);
 }
 };

 if (loading) return <p>Loading Measurement Sheets...</p>;

 return (
 <Fragment>
 {/* =========================
 PDF PREVIEW LOADER
 ========================== */}
 {previewing && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing JMR PDF…</h6>
 </div>
 </div>
 )}
 <Row className="g-4">
 {/* SUMMARY CARD */}
 <Col md={12}>
 <div className="d-flex justify-content-between align-items-center mb-3">
 <h4 className="fw-bold text-primary mb-0">Measurement Sheets (JMR)</h4>
 <Button 
 variant="primary" 
 className="px-4 shadow-sm"
 onClick={() => navigate(`/measurement-sheets/add?workOrder=${workOrderId}`)}
 >
 <i className="fa fa-plus me-2"></i> Add Measurement Sheet
 </Button>
 </div>
 </Col>

 <Col md={4}>
 <Card className="shadow-sm border-0 h-100 bg-light">
 <Card.Body>
 <h5 className="fw-bold mb-3">Statistics</h5>
 <div className="d-flex justify-content-between mb-2">
 <span>Total Sheets:</span>
 <span className="fw-bold">{stats.total}</span>
 </div>
 <div className="d-flex justify-content-between mb-2 text-success">
 <span>Approved:</span>
 <span className="fw-bold">{stats.approved}</span>
 </div>
 <div className="d-flex justify-content-between mb-3 text-warning">
 <span>Pending:</span>
 <span className="fw-bold">{stats.pending}</span>
 </div>
 <ProgressBar
 variant="success"
 now={approvalPercent}
 label={`${approvalPercent.toFixed(0)}%`}
 style={{ height: '10px' }}
 />
 </Card.Body>
 </Card>
 </Col>

 {/* LIST TABLE */}
 <Col md={12}>
 <Card className="shadow-sm border-0">
 <Card.Body className="p-0">
 <Table hover responsive className="mb-0 align-middle">
 <thead className="bg-light">
 <tr>
 <th className="ps-4">#</th>
 <th>Sheet No</th>
 <th>Type</th>
 <th className="text-center">Status</th>
 <th className="text-center">Verified Copy</th>
 <th className="text-center">Billing</th>
 <th>Date</th>
 <th className="text-end pe-4">Actions</th>
 </tr>
 </thead>
 <tbody>
 {sheets.length === 0 ? (
 <tr><td colSpan={8} className="text-center py-5 text-muted">No measurement sheets found for this Work Order</td></tr>
 ) : sheets.map((s, i) => (
 <tr key={s._id}>
 <td className="ps-4">{i + 1}</td>
 <td><strong className="text-primary">{s.sheetNumber}</strong></td>
 <td>
 <Badge className="rounded-pill" bg="info">
 {(() => {
 const names = {
 ELECTRICAL_LIGHTING: "Electrical Lighting",
 CONDUIT_MEASUREMENT: "Conduit Measurement",
 CONDUIT_SUMMARY: "Conduit Summary",
 SWITCH_SOCKET: "Switch & Socket",
 DB_INSTALLATION: "DB Installation",
 CABLE_MEASUREMENT: "Cable Measurement",
 CABLE_TRAY_SUMMARY: "Cable Tray Summary",
 WIRING_MEASUREMENT: "Wiring Measurement"
 };
 return names[s.sheetType] || s.sheetType;
 })()}
 </Badge>
 </td>
 <td className="text-center"><Badge className="rounded-pill" bg={getBadge(s.status)}>{s.status}</Badge></td>
 <td className="text-center">
 <VerifiedCopyActions 
 id={s._id} 
 type="measurement-sheets" 
 hasVerifiedCopy={!!s.verifiedCopy?.filePath} 
 onUploadSuccess={fetchSheets}
 />
 </td>
 <td className="text-center">
 {billedIds.has(String(s._id)) ? (
 <Badge className="rounded-pill" bg="success">Billed</Badge>
 ) : (
 <Badge className="rounded-pill" bg="secondary">Unbilled</Badge>
 )}
 </td>
 <td>{formatDate(s.preparedDate)}</td>
 <td className="text-end pe-4">
 <Button 
 variant="outline-danger" 
 size="sm" 
 className="me-1 border-0"
 title="Download PDF"
 onClick={() => handleDownloadFile(s._id, 'pdf')}
 >
 <i className="fas fa-file-pdf"></i>
 </Button>
 <Button 
 variant="outline-success" 
 size="sm" 
 className="me-1 border-0"
 title="Download Excel"
 onClick={() => handleDownloadFile(s._id, 'excel')}
 >
 <i className="fas fa-file-excel"></i>
 </Button>
 <Button 
 variant="outline-primary" 
 size="sm"
 className="border-0 me-1"
 title="Edit"
 onClick={() => navigate(`/measurement-sheets/edit/${s._id}`)}
 >
 <i className="fas fa-edit"></i>
 </Button>
 <Button 
 variant="outline-danger" 
 size="sm"
 className="border-0"
 title="Delete"
 onClick={() => handleDeleteSheet(s._id)}
 >
 <i className="fas fa-trash"></i>
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default MeasurementSheetTab;
